import React, {useEffect, useState} from 'react';
import {Divider, Grid, Tab, Tabs} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Api from '../../../store/Api';
import {DuplicateCheckLimits, DuplicateCheckParams, LlmConfig} from '../../../store/models/DuplicateCheck';
import {DEFAULT_MAX_TOP_N, LLM_STORAGE_KEY, WEIGHT_DEFAULTS_PCT} from './duplicateCheck/constants';
import SingleCheckForm from './duplicateCheck/SingleCheckForm';
import BatchRunPanel from './duplicateCheck/BatchRunPanel';
import LlmJsonCheckPanel from './duplicateCheck/LlmJsonCheckPanel';

const useStyles = makeStyles((theme) => ({
    container: {
        marginTop: theme.spacing(2),
    },
}));

interface StoredLlmSettings {
    preset?: string;
}

function readStoredLlmSettings(): Partial<StoredLlmSettings> {
    try {
        const raw = localStorage.getItem(LLM_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function writeStoredLlmSettings(settings: StoredLlmSettings) {
    try {
        localStorage.setItem(LLM_STORAGE_KEY, JSON.stringify(settings));
    } catch {
        // localStorage not available
    }
}

export default function BadDuplicateCheckTab() {
    const classes = useStyles();

    const [activeMode, setActiveMode] = useState<'single' | 'llmJson' | 'batch'>('single');
    const [form, setForm] = useState<DuplicateCheckParams>({topN: 5});
    const [weightsOpen, setWeightsOpen] = useState(false);
    const [weightValues, setWeightValues] = useState<Record<string, number>>({...WEIGHT_DEFAULTS_PCT});
    const [limits, setLimits] = useState<DuplicateCheckLimits | null>(null);

    const [storedLlm] = useState(readStoredLlmSettings);
    const [llmConfig, setLlmConfig] = useState<LlmConfig | null>(null);
    const [llmPreset, setLlmPreset] = useState<string | undefined>(storedLlm.preset);

    const maxTopN = limits?.maxTopN ?? DEFAULT_MAX_TOP_N;

    useEffect(() => {
        setForm((prev) => (prev.topN != null && prev.topN > maxTopN ? {...prev, topN: maxTopN} : prev));
    }, [maxTopN]);

    useEffect(() => {
        Api.getDuplicateCheckLlmConfig()
            .then((config) => {
                setLlmConfig(config);
                // gespeicherte Auswahl nur übernehmen, solange es das Preset noch gibt
                setLlmPreset((prev) => (config.presets.some((p) => p.name === prev) ? prev : config.defaultPreset));
            })
            .catch(() => setLlmConfig(null));
    }, []);

    const handleLlmPresetChange = (preset: string) => {
        setLlmPreset(preset);
        writeStoredLlmSettings({preset});
    };

    const weightsPayload = Object.fromEntries(
        Object.entries(weightValues).map(([k, v]) => [k, v / 100]),
    );

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        if (name === 'lat' || name === 'lng') {
            setForm((prev) => ({...prev, [name]: value === '' ? undefined : parseFloat(value)}));
        } else if (name === 'topN') {
            const parsed = value === '' ? undefined : Math.max(1, Math.min(maxTopN, parseInt(value, 10) || 1));
            setForm((prev) => ({...prev, topN: parsed}));
        } else {
            setForm((prev) => ({...prev, [name]: value === '' ? undefined : value}));
        }
    };

    const handleWeightChange = (key: string, raw: string) => {
        const parsed = raw === '' ? 0 : Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
        setWeightValues((prev) => ({...prev, [key]: parsed}));
    };

    const handleResetWeights = () => setWeightValues({...WEIGHT_DEFAULTS_PCT});

    const sharedProps = {
        maxTopN,
        weightsOpen,
        onWeightsToggle: () => setWeightsOpen((o) => !o),
        weightValues,
        onWeightChange: handleWeightChange,
        onWeightsReset: handleResetWeights,
        weightsPayload,
        onLimits: setLimits,
    };

    const llmProps = {
        llmConfig,
        llmPreset,
        onLlmPresetChange: handleLlmPresetChange,
    };

    return (
        <Grid className={classes.container} container spacing={3}>
            <Grid item xs={12}>
                <Tabs value={activeMode} indicatorColor="primary" onChange={(_, v) => setActiveMode(v)}>
                    <Tab value="single" label="Einzelabfrage"/>
                    <Tab value="llmJson" label="LLM-Prüfung (JSON)"/>
                    <Tab value="batch" label="Batch (Google Places)"/>
                </Tabs>
                <Divider/>
            </Grid>

            {activeMode === 'single' && (
                <Grid item xs={12}>
                    <SingleCheckForm
                        form={form}
                        onFormChange={handleFormChange}
                        {...sharedProps}
                        {...llmProps}
                    />
                </Grid>
            )}

            {activeMode === 'llmJson' && (
                <Grid item xs={12}>
                    <LlmJsonCheckPanel {...llmProps}/>
                </Grid>
            )}

            {activeMode === 'batch' && (
                <Grid item xs={12}>
                    <BatchRunPanel
                        topN={form.topN}
                        onFormChange={handleFormChange}
                        limits={limits}
                        {...sharedProps}
                    />
                </Grid>
            )}
        </Grid>
    );
}
